import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from 'components/AppIcon';

const GOAL_TYPE_CONFIG = {
  vacation: { icon: 'Plane', color: '#00E5FF', label: 'Vacation' },
  emergency_fund: { icon: 'Shield', color: '#7B2FBE', label: 'Emergency Fund' },
  debt_repayment: { icon: 'CreditCard', color: '#F59E0B', label: 'Debt Repayment' },
  home_down_payment: { icon: 'Home', color: '#10B981', label: 'Home Down Payment' },
  custom: { icon: 'Target', color: '#06B6D4', label: 'Custom' },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.2)] rounded-lg p-3 shadow-xl">
      <p className="text-xs text-[#64748B] mb-1">{label}</p>
      <p className="font-data text-sm font-semibold text-[#00E5FF]">${payload?.[0]?.value?.toLocaleString()}</p>
    </div>
  );
};

export default function GoalDetailDrawer({ goal, isOpen, onClose, onContribute }) {
  const [chartData, setChartData] = useState([]);

  const typeKey = goal?.type || 'custom';
  const config = GOAL_TYPE_CONFIG?.[typeKey] || GOAL_TYPE_CONFIG?.custom;
  const color = goal?.color || config?.color;
  const target = parseFloat(goal?.target_amount) || 1;
  const current = parseFloat(goal?.current_amount) || 0;
  const pct = Math.min((current / target) * 100, 100);
  const monthly = parseFloat(goal?.monthly_contribution) || 0;

  useEffect(() => {
    if (!goal || !isOpen) return;
    // Generate simulated contribution history based on current amount and monthly contribution
    const months = 6;
    const data = [];
    const startAmount = Math.max(0, current - monthly * months);
    for (let i = months; i >= 0; i--) {
      const d = new Date();
      d?.setMonth(d?.getMonth() - i);
      const amount = Math.min(target, startAmount + monthly * (months - i));
      data?.push({
        month: d?.toLocaleDateString('default', { month: 'short' }),
        amount: Math.round(amount),
      });
    }
    setChartData(data);
  }, [goal, isOpen]);

  const getStatusBadge = () => {
    if (pct >= 100) return { label: 'Completed', color: '#10B981', bg: 'rgba(16,185,129,0.12)' };
    if (!goal?.deadline) return { label: 'Active', color: '#06B6D4', bg: 'rgba(6,182,212,0.12)' };
    const daysLeft = Math.max(0, Math.ceil((new Date(goal.deadline) - new Date()) / (1000 * 60 * 60 * 24)));
    const remaining = target - current;
    const monthsLeft = daysLeft / 30;
    if (monthly * monthsLeft >= remaining) return { label: 'On Track', color: '#10B981', bg: 'rgba(16,185,129,0.12)' };
    if (monthly * monthsLeft >= remaining * 0.8) return { label: 'Behind', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' };
    return { label: 'At Risk', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' };
  };

  const getMilestones = () => [
    { pct: 25, label: '25% reached', reached: pct >= 25 },
    { pct: 50, label: 'Halfway there!', reached: pct >= 50 },
    { pct: 75, label: '75% milestone', reached: pct >= 75 },
    { pct: 100, label: 'Goal achieved! 🎉', reached: pct >= 100 },
  ];

  const status = getStatusBadge();
  const milestones = getMilestones();

  if (!isOpen || !goal) return null;

  return (
    <div className="fixed inset-0 z-[150] flex">
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-[#0D1B2A] border-l border-[rgba(6,182,212,0.15)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[rgba(6,182,212,0.1)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20`, border: `1px solid ${color}30` }}>
              <Icon name={goal?.icon || config?.icon} size={20} color={color} />
            </div>
            <div>
              <h2 className="font-heading font-bold text-base text-[#F8FAFC]">{goal?.title}</h2>
              <p className="text-xs text-[#64748B]">{config?.label}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ color: status?.color, backgroundColor: status?.bg }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status?.color }} />
              {status?.label}
            </span>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[rgba(6,182,212,0.1)] transition-colors">
              <Icon name="X" size={16} color="#64748B" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Progress Overview */}
          <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-4">
            <div className="flex items-end justify-between mb-3">
              <div>
                <p className="text-xs text-[#64748B] mb-1">Progress</p>
                <p className="font-data text-2xl font-bold text-[#F8FAFC]">
                  ${current?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  <span className="text-sm text-[#64748B] font-normal ml-1">/ ${target?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </p>
              </div>
              <span className="font-data text-xl font-bold" style={{ color }}>{pct?.toFixed(1)}%</span>
            </div>
            <div className="h-3 bg-[#1E293B] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}80, ${color})` }}
              />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            {goal?.deadline && (
              <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-3">
                <p className="text-xs text-[#64748B] mb-1">Deadline</p>
                <p className="font-data text-sm font-semibold text-[#F8FAFC]">
                  {new Date(goal.deadline)?.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            )}
            {monthly > 0 && (
              <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-3">
                <p className="text-xs text-[#64748B] mb-1">Monthly Contribution</p>
                <p className="font-data text-sm font-semibold" style={{ color }}>${monthly?.toFixed(0)}/mo</p>
              </div>
            )}
            <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-3">
              <p className="text-xs text-[#64748B] mb-1">Remaining</p>
              <p className="font-data text-sm font-semibold text-[#F8FAFC]">${Math.max(0, target - current)?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            </div>
            <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-3">
              <p className="text-xs text-[#64748B] mb-1">Started</p>
              <p className="font-data text-sm font-semibold text-[#F8FAFC]">
                {goal?.created_at ? new Date(goal.created_at)?.toLocaleDateString('default', { month: 'short', year: 'numeric' }) : 'N/A'}
              </p>
            </div>
          </div>

          {/* Contribution History Chart */}
          <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-4">
            <h3 className="font-heading font-semibold text-sm text-[#F8FAFC] mb-4">Contribution History</h3>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(6,182,212,0.06)" />
                  <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000)?.toFixed(0)}k` : v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="amount" stroke={color} strokeWidth={2} dot={{ fill: color, r: 3 }} activeDot={{ r: 5, fill: color }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Milestones */}
          <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-4">
            <h3 className="font-heading font-semibold text-sm text-[#F8FAFC] mb-4">Milestones</h3>
            <div className="space-y-3">
              {milestones?.map((m, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: m?.reached ? `${color}20` : 'rgba(30,41,59,0.8)',
                      border: `1px solid ${m?.reached ? color : 'rgba(6,182,212,0.1)'}`,
                    }}
                  >
                    {m?.reached ? (
                      <Icon name="Check" size={13} color={color} />
                    ) : (
                      <span className="text-[9px] font-bold text-[#475569]">{m?.pct}%</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="h-1 bg-[#1E293B] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(pct / m?.pct * 100, 100)}%`, backgroundColor: m?.reached ? color : '#334155' }}
                      />
                    </div>
                  </div>
                  <span className="text-xs" style={{ color: m?.reached ? color : '#475569' }}>{m?.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[rgba(6,182,212,0.1)]">
          <button
            onClick={() => onContribute?.(goal)}
            className="w-full py-3 rounded-xl text-sm font-semibold text-[#0F172A] flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90"
            style={{ background: `linear-gradient(135deg, ${color}, #06B6D4)` }}
          >
            <Icon name="Plus" size={16} color="#0F172A" />
            Contribute to this Goal
          </button>
        </div>
      </div>
    </div>
  );
}
